import React from "react";
import { useSearchParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import { AddOnProtectedRoute } from "@/components/addon/AddOnProtectedRoute";
import { AddOnType } from "@/model/interface/AddOn";
import { MyPhoneNumbers } from "../phone-numbers/MyPhoneNumbers";
import { NewPhoneNumber } from "../phone-numbers/NewPhoneNumber";
import DialerNumbersTab from "./DialerNumbersTab";

const NUMBER_VIEWS = [
  { value: "mine", label: "My numbers" },
  { value: "dialer", label: "Dialer numbers" },
  { value: "purchase", label: "Purchase" },
] as const;
type NumberView = (typeof NUMBER_VIEWS)[number]["value"];

const PhoneNumbersContent: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const numbersParam = searchParams.get("numbers");
  const activeView: NumberView = NUMBER_VIEWS.find((view) => view.value === numbersParam)?.value ?? "mine";

  const showView = (view: NumberView) => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set("numbers", view);
        return next;
      },
      { replace: true },
    );
  };

  return (
    <div className="space-y-6">
      <div className="inline-flex items-center gap-1 rounded-lg border bg-card p-1">
        {NUMBER_VIEWS.map((view) => {
          const isActive = view.value === activeView;
          return (
            <button
              key={view.value}
              type="button"
              aria-pressed={isActive}
              onClick={() => showView(view.value)}
              className={cn(
                "rounded-md border px-3.5 py-1.5 text-sm font-medium",
                isActive
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-transparent text-muted-foreground hover:bg-muted",
              )}
            >
              {view.label}
            </button>
          );
        })}
      </div>

      {activeView === "mine" && <MyPhoneNumbers onSwitchToPurchase={() => showView("purchase")} />}
      {activeView === "dialer" && (
        <AddOnProtectedRoute
          addOns={[AddOnType.POWER_DIALER_SINGLE, AddOnType.POWER_DIALER_MULTI]}
          title="Dialer numbers"
          description="See how each number your power dialer calls from is doing, rest numbers that look flagged, and set daily call limits. Dialer numbers are part of the Power Dialer add-on."
        >
          <DialerNumbersTab />
        </AddOnProtectedRoute>
      )}
      {activeView === "purchase" && <NewPhoneNumber />}
    </div>
  );
};

export default PhoneNumbersContent;
